import { ReactNode, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Button,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  MultiSelect,
  Paper,
  Progress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { BarChart, LineChart } from '@mantine/charts';
import {
  IconAlertCircle,
  IconArrowDownRight,
  IconArrowUpRight,
  IconScale,
  IconBuildingStore,
} from '@tabler/icons-react';
import {
  useIncomeExpensesAnalytics,
  usePaidDocumentsRatio,
  useCostCenterBreakdown,
} from '@api/analytics';
import { useCompaniesBySearch } from '@api/companies';
import { useCostCenters } from '@api/costCenters';
import { PaidDocumentsRatioMetrics } from '@types';
import { useAuth } from '@providers/auth';

const currentMonth = dayjs().format('YYYY-MM');
const defaultStartMonth = dayjs().subtract(2, 'month').format('YYYY-MM');

const formatCurrency = (value: number) =>
  `$ ${Math.round(value).toLocaleString('es-CL', {
    maximumFractionDigits: 0,
  })}`;

const formatPercentage = (value: number) =>
  `${value.toLocaleString('es-CL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

const formatMonthLabel = (month: string) => {
  if (!month) return 'Mes no seleccionado';
  const date = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Mes invalido';
  const label = new Intl.DateTimeFormat('es-CL', { month: 'short', year: 'numeric' }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getErrorMessage = (error: unknown) => {
  const typedError = error as {
    response?: { data?: { error?: { message?: string }; message?: string } };
  };
  return (
    typedError?.response?.data?.error?.message ||
    typedError?.response?.data?.message ||
    'No se pudo cargar la información.'
  );
};

type LineFilter = 'all' | 'income' | 'expense' | 'balance';

const incomeExpensesSeries = [
  { name: 'income', label: 'Ingresos', color: 'teal.6' },
  { name: 'expense', label: 'Egresos', color: 'red.6' },
  { name: 'balance', label: 'Balance', color: 'blue.6' },
];

const emptyPaidMetrics: PaidDocumentsRatioMetrics = {
  total_documents: 0,
  paid_documents: 0,
  paid_percentage: 0,
};

const aggregatePaidMetrics = (metrics: PaidDocumentsRatioMetrics[]) => {
  const totalDocuments = metrics.reduce((sum, item) => sum + (item?.total_documents ?? 0), 0);
  const paidDocuments = metrics.reduce((sum, item) => sum + (item?.paid_documents ?? 0), 0);
  return {
    total_documents: totalDocuments,
    paid_documents: paidDocuments,
    paid_percentage:
      totalDocuments > 0 ? Number(((paidDocuments / totalDocuments) * 100).toFixed(2)) : 0,
  };
};

function MetricCard({
  title,
  value,
  color,
  icon,
  paymentMetrics,
  paymentColor,
  paymentRangeLabel,
}: {
  title: string;
  value: string;
  color: string;
  icon: ReactNode;
  paymentMetrics?: PaidDocumentsRatioMetrics;
  paymentColor: string;
  paymentRangeLabel: string;
}) {
  const totalDocuments = paymentMetrics?.total_documents ?? 0;
  const paidDocuments = paymentMetrics?.paid_documents ?? 0;
  const paidPercentage = paymentMetrics?.paid_percentage ?? 0;
  const tooltipLabel =
    totalDocuments > 0
      ? `${paidDocuments} pagados de ${totalDocuments} totales`
      : 'No hay documentos pagables para este rango';

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Text c="dimmed" fz="sm" fw={500}>
            {title}
          </Text>
          <Text fw={700} fz={28}>
            {value}
          </Text>
        </div>
        <ThemeIcon color={color} variant="light" size="lg" radius="md">
          {icon}
        </ThemeIcon>
      </Group>
      <Stack gap={6}>
        <Group justify="space-between" align="center">
          <Text fz="sm" c="dimmed">
            Pago {paymentRangeLabel}
          </Text>
          <Text fz="sm" fw={600}>
            {formatPercentage(paidPercentage)}
          </Text>
        </Group>
        <Tooltip label={tooltipLabel} withArrow>
          <Progress value={paidPercentage} color={paymentColor} radius="xl" size="md" />
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function Summary() {
  const { session } = useAuth();
  const costCentersAvailable = !!session.activeAccount?.cost_centers_available;
  const [startMonth, setStartMonth] = useState(defaultStartMonth);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ label: string; value: string }[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(companySearch, 400);

  // Cost center cascade filters
  const [selectedL1Id, setSelectedL1Id] = useState<string | null>(null);
  const [selectedL2Id, setSelectedL2Id] = useState<string | null>(null);
  const [selectedL3Id, setSelectedL3Id] = useState<string | null>(null);

  const { data: costCentersData } = useCostCenters();
  const allCostCenters = costCentersData?.data ?? [];
  const activeCostCenters = allCostCenters.filter((c) => !c.discarded_at);

  const l1Options = activeCostCenters
    .filter((c) => c.level === 1)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const l2Options = activeCostCenters
    .filter((c) => c.level === 2 && (!selectedL1Id || c.parent_id === Number(selectedL1Id)))
    .map((c) => {
      if (selectedL1Id) return { value: String(c.id), label: c.name };
      const parent = activeCostCenters.find((p) => p.id === c.parent_id);
      return { value: String(c.id), label: parent ? `${parent.name} / ${c.name}` : c.name };
    });

  const l3Options = activeCostCenters
    .filter((c) => c.level === 3 && (!selectedL2Id || c.parent_id === Number(selectedL2Id)))
    .map((c) => {
      if (selectedL2Id) return { value: String(c.id), label: c.name };
      const parent = activeCostCenters.find((p) => p.id === c.parent_id);
      return { value: String(c.id), label: parent ? `${parent.name} / ${c.name}` : c.name };
    });

  const effectiveCostCenterId = selectedL3Id ?? selectedL2Id ?? selectedL1Id ?? null;

  const hasCostCenters = l1Options.length > 0;

  const { data: companiesData, isFetching: isCompaniesSearching } =
    useCompaniesBySearch(debouncedSearch);

  const searchResults = useMemo(
    () =>
      companiesData?.data?.map((company) => ({
        label: company.legal_name as string,
        value: String(company.id),
      })) ?? [],
    [companiesData?.data],
  );

  const companyOptions = useMemo(() => {
    const merged = [...selectedOptions, ...searchResults];
    const seen = new Set<string>();
    return merged.filter(({ value }) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }, [selectedOptions, searchResults]);

  const handleCompanyChange = (values: string[]) => {
    setSelectedCompanyIds(values);
    setSelectedOptions(
      values.map((v) => companyOptions.find((o) => o.value === v) ?? { label: v, value: v }),
    );
  };

  const isRangeInvalid = Boolean(startMonth && endMonth && startMonth > endMonth);

  const {
    data: incomeExpenses,
    isLoading: isIncomeExpensesLoading,
    isFetching: isIncomeExpensesFetching,
    error: incomeExpensesError,
  } = useIncomeExpensesAnalytics({
    startMonth,
    endMonth,
    enabled: !isRangeInvalid,
    companyIds: selectedCompanyIds,
    costCenterId: effectiveCostCenterId,
  });

  const {
    data: paidRatio,
    isLoading: isPaidRatioLoading,
    isFetching: isPaidRatioFetching,
    error: paidRatioError,
  } = usePaidDocumentsRatio({
    startMonth,
    endMonth,
    enabled: !isRangeInvalid,
    companyIds: selectedCompanyIds,
    costCenterId: effectiveCostCenterId,
  });

  const { data: breakdownData, isFetching: isBreakdownFetching } = useCostCenterBreakdown({
    startMonth,
    endMonth,
    enabled: !isRangeInvalid && hasCostCenters,
    companyIds: selectedCompanyIds,
  });

  const monthlyData = useMemo(() => incomeExpenses?.months ?? [], [incomeExpenses?.months]);

  const totals = useMemo(
    () =>
      monthlyData.reduce(
        (accumulator, item) => ({
          income: accumulator.income + item.income,
          expense: accumulator.expense + item.expense,
          balance: accumulator.balance + item.balance,
        }),
        { income: 0, expense: 0, balance: 0 },
      ),
    [monthlyData],
  );

  const chartData = useMemo(
    () => monthlyData.map((item) => ({ ...item, label: formatMonthLabel(item.month) })),
    [monthlyData],
  );

  const visibleLineSeries = useMemo(() => {
    if (lineFilter === 'all') return incomeExpensesSeries;
    return incomeExpensesSeries.filter((series) => series.name === lineFilter);
  }, [lineFilter]);

  const paymentRangeLabel = useMemo(
    () => `${formatMonthLabel(startMonth)} - ${formatMonthLabel(endMonth)}`,
    [startMonth, endMonth],
  );

  const paidRatioMonths = useMemo(() => paidRatio?.months ?? [], [paidRatio?.months]);

  const emittedPaymentMetrics = useMemo<PaidDocumentsRatioMetrics>(
    () => aggregatePaidMetrics(paidRatioMonths.map((item) => item.emitted ?? emptyPaidMetrics)),
    [paidRatioMonths],
  );

  const receivedPaymentMetrics = useMemo<PaidDocumentsRatioMetrics>(
    () => aggregatePaidMetrics(paidRatioMonths.map((item) => item.received ?? emptyPaidMetrics)),
    [paidRatioMonths],
  );

  const balancePaymentMetrics = useMemo<PaidDocumentsRatioMetrics>(() => {
    const emittedTotal = emittedPaymentMetrics.total_documents ?? 0;
    const receivedTotal = receivedPaymentMetrics.total_documents ?? 0;
    const emittedPaid = emittedPaymentMetrics.paid_documents ?? 0;
    const receivedPaid = receivedPaymentMetrics.paid_documents ?? 0;
    const totalDocuments = emittedTotal + receivedTotal;
    const paidDocuments = emittedPaid + receivedPaid;
    return {
      total_documents: totalDocuments,
      paid_documents: paidDocuments,
      paid_percentage:
        totalDocuments > 0 ? Number(((paidDocuments / totalDocuments) * 100).toFixed(2)) : 0,
    };
  }, [emittedPaymentMetrics, receivedPaymentMetrics]);

  const breakdownChartData = useMemo(
    () =>
      (breakdownData?.cost_centers ?? []).map((cc) => ({
        name: cc.name,
        Ingresos: cc.income,
        Egresos: cc.expense,
      })),
    [breakdownData?.cost_centers],
  );

  const handleResetRange = () => {
    setStartMonth(defaultStartMonth);
    setEndMonth(currentMonth);
  };

  return (
    <Container fluid pb="xl">
      <Stack gap="lg">
        <div>
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <div>
              <Title order={1}>Resumen</Title>
              <Text c="dimmed">
                Visualiza la evolución de ingresos, egresos y balance con su avance de pago.
              </Text>
            </div>
            <Badge color="yellow" variant="light" leftSection={<IconAlertCircle size={14} />}>
              Beta: puede contener errores
            </Badge>
          </Group>
        </div>

        <Paper withBorder radius="lg" p="lg">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <div>
                <Text fw={700} fz="xl">
                  Ingresos y egresos
                </Text>
                <Text c="dimmed" fz="sm">
                  Analiza ingresos, egresos y balance dentro del rango seleccionado.
                </Text>
              </div>
              <Button variant="light" onClick={handleResetRange}>
                Últimos 3 meses
              </Button>
            </Group>
            <Group gap="sm" align="end" wrap="wrap">
              <MultiSelect
                data={companyOptions}
                value={selectedCompanyIds}
                onChange={handleCompanyChange}
                searchValue={companySearch}
                onSearchChange={setCompanySearch}
                label="Cliente / Proveedor"
                placeholder="Todos"
                searchable
                clearable
                maw={280}
                filter={({ options }) => options}
                nothingFoundMessage={isCompaniesSearching ? 'Buscando...' : 'No encontrado...'}
              />
              {costCentersAvailable && hasCostCenters && (
                <>
                  <Select
                    label="Plan de cuenta (N1)"
                    placeholder="Todos"
                    clearable
                    data={l1Options}
                    value={selectedL1Id}
                    onChange={(v) => {
                      setSelectedL1Id(v);
                      setSelectedL2Id(null);
                      setSelectedL3Id(null);
                    }}
                    maw={180}
                    leftSection={<IconBuildingStore size={14} />}
                  />
                  <Select
                    label="Plan de cuenta (N2)"
                    placeholder="Todos"
                    clearable
                    data={l2Options}
                    value={selectedL2Id}
                    disabled={l2Options.length === 0}
                    onChange={(v) => {
                      setSelectedL2Id(v);
                      setSelectedL3Id(null);
                    }}
                    maw={180}
                  />
                  <Select
                    label="Plan de cuenta (N3)"
                    placeholder="Todos"
                    clearable
                    data={l3Options}
                    value={selectedL3Id}
                    disabled={l3Options.length === 0}
                    onChange={setSelectedL3Id}
                    maw={180}
                  />
                </>
              )}
              <TextInput
                type="month"
                label="Desde"
                value={startMonth}
                onChange={(event) => setStartMonth(event.currentTarget.value)}
                max={endMonth}
                maw={170}
              />
              <TextInput
                type="month"
                label="Hasta"
                value={endMonth}
                onChange={(event) => setEndMonth(event.currentTarget.value)}
                min={startMonth}
                maw={170}
              />
            </Group>

            <Divider />

            <SimpleGrid cols={{ base: 1, md: 3 }}>
              <MetricCard
                title="Ingresos acumulados"
                value={formatCurrency(totals.income)}
                color="teal"
                icon={<IconArrowUpRight size={18} />}
                paymentMetrics={emittedPaymentMetrics}
                paymentColor="teal"
                paymentRangeLabel={paymentRangeLabel}
              />
              <MetricCard
                title="Egresos acumulados"
                value={formatCurrency(totals.expense)}
                color="red"
                icon={<IconArrowDownRight size={18} />}
                paymentMetrics={receivedPaymentMetrics}
                paymentColor="red"
                paymentRangeLabel={paymentRangeLabel}
              />
              <MetricCard
                title="Balance acumulado"
                value={formatCurrency(totals.balance)}
                color={totals.balance >= 0 ? 'blue' : 'orange'}
                icon={<IconScale size={18} />}
                paymentMetrics={balancePaymentMetrics}
                paymentColor="blue"
                paymentRangeLabel={paymentRangeLabel}
              />
            </SimpleGrid>

            {paidRatioError ? (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {getErrorMessage(paidRatioError)}
              </Alert>
            ) : null}

            <Paper withBorder radius="md" p="lg" pos="relative">
              <LoadingOverlay
                visible={
                  isIncomeExpensesLoading ||
                  isIncomeExpensesFetching ||
                  isPaidRatioLoading ||
                  isPaidRatioFetching
                }
                zIndex={1}
              />
              <Stack gap="md">
                <Group justify="space-between" align="center" wrap="wrap">
                  <div>
                    <Text fw={700} fz="lg">
                      Evolución mensual
                    </Text>
                    <Text c="dimmed" fz="sm">
                      {formatMonthLabel(startMonth)} - {formatMonthLabel(endMonth)}
                    </Text>
                  </div>
                  <SegmentedControl
                    value={lineFilter}
                    onChange={(value) => setLineFilter(value as LineFilter)}
                    data={[
                      { label: 'Todas', value: 'all' },
                      { label: 'Ingresos', value: 'income' },
                      { label: 'Egresos', value: 'expense' },
                      { label: 'Balance', value: 'balance' },
                    ]}
                  />
                </Group>

                {isRangeInvalid ? (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                    El mes inicial no puede ser mayor al mes final.
                  </Alert>
                ) : incomeExpensesError ? (
                  <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                    {getErrorMessage(incomeExpensesError)}
                  </Alert>
                ) : chartData.length > 0 ? (
                  <LineChart
                    h={340}
                    data={chartData}
                    dataKey="label"
                    series={visibleLineSeries}
                    withLegend={lineFilter === 'all'}
                    gridAxis="y"
                    tickLine="y"
                    strokeDasharray="4 4"
                    yAxisProps={{ width: 110 }}
                    valueFormatter={formatCurrency}
                    referenceLines={[{ y: 0, color: 'gray.5' }]}
                    curveType="monotone"
                    withDots={false}
                    strokeWidth={3}
                  />
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                    No hay información de ingresos y egresos para el rango seleccionado.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        {costCentersAvailable && hasCostCenters && (
          <Paper withBorder radius="lg" p="lg">
            <Stack gap="lg">
              <div>
                <Group gap="xs" mb={4}>
                  <IconBuildingStore size={20} />
                  <Text fw={700} fz="xl">
                    Distribución por plan de cuenta
                  </Text>
                </Group>
                <Text c="dimmed" fz="sm">
                  Ingresos y egresos por plan de cuenta de nivel 1 en el período seleccionado. No se
                  ve afectado por el filtro de plan de cuenta.
                </Text>
              </div>

              <Paper withBorder radius="md" p="lg" pos="relative">
                <LoadingOverlay visible={isBreakdownFetching} zIndex={1} />
                {breakdownChartData.length > 0 ? (
                  <BarChart
                    h={300}
                    data={breakdownChartData}
                    dataKey="name"
                    series={[
                      { name: 'Ingresos', color: 'teal.6' },
                      { name: 'Egresos', color: 'red.6' },
                    ]}
                    withLegend
                    gridAxis="y"
                    tickLine="y"
                    yAxisProps={{ width: 110 }}
                    valueFormatter={formatCurrency}
                    barProps={{ radius: 4 }}
                  />
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                    No hay documentos asignados a planes de cuenta en el rango seleccionado.
                  </Alert>
                )}
              </Paper>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default Summary;
